// Zona de upload de documentos para el portal del cliente
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  CloudArrowUpIcon,
  DocumentPlusIcon,
} from '@heroicons/react/24/outline';

const TIPOS_DOCUMENTO = [
  { value: '', label: 'Seleccionar tipo (opcional)' },
  { value: 'pasaporte', label: 'Pasaporte' },
  { value: 'nie', label: 'NIE / TIE' },
  { value: 'dni', label: 'DNI' },
  { value: 'empadronamiento', label: 'Certificado de empadronamiento' },
  { value: 'contrato_trabajo', label: 'Contrato de trabajo' },
  { value: 'nominas', label: 'Nóminas' },
  { value: 'seguro_medico', label: 'Seguro médico' },
  { value: 'antecedentes_penales', label: 'Antecedentes penales' },
  { value: 'titulo_academico', label: 'Título académico' },
  { value: 'certificado_matrimonio', label: 'Certificado de matrimonio' },
  { value: 'libro_familia', label: 'Libro de familia' },
  { value: 'declaracion_renta', label: 'Declaración de la renta' },
  { value: 'vida_laboral', label: 'Vida laboral' },
  { value: 'otro', label: 'Otro documento' },
];

const ACCEPT = 'image/*,.pdf,.doc,.docx';
const MAX_SIZE_MB = 10;

interface Props {
  onUpload: (file: File, tipoDocumento?: string) => void;
  isUploading?: boolean;
}

export function ClienteDocumentUpload({ onUpload, isUploading }: Props) {
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    setError(null);
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`El archivo supera ${MAX_SIZE_MB}MB`);
      return;
    }
    onUpload(file, tipoDocumento || undefined);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <DocumentPlusIcon className="h-5 w-5" />
          Subir documento
        </h3>

        {/* Selector de tipo */}
        <select
          value={tipoDocumento}
          onChange={(e) => setTipoDocumento(e.target.value)}
          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
        >
          {TIPOS_DOCUMENTO.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragActive
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 dark:border-gray-700 hover:border-primary/50'
          }`}
        >
          <CloudArrowUpIcon className="h-10 w-10 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {isUploading ? 'Subiendo...' : 'Arrastra un archivo aquí o haz clic para seleccionar'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            PDF, imágenes o documentos Word (máx. {MAX_SIZE_MB}MB)
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={handleChange}
            className="hidden"
            disabled={isUploading}
          />
        </div>

        {/* Botón alternativo para móvil */}
        <Button
          variant="outline"
          className="w-full sm:hidden"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          <CloudArrowUpIcon className="h-4 w-4 mr-2" />
          {isUploading ? 'Subiendo...' : 'Seleccionar archivo'}
        </Button>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </CardContent>
    </Card>
  );
}
